import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, X, Upload, Loader2 } from "lucide-react";
import api from "@/lib/api";

const EMPTY = { name: "", category: "cakes", price: 0, image: "", description: "", weight_options: [], tags: "", occasions: "", in_stock: true };
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function absImg(url) {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing({
      ...p,
      weight_options: (p.weight_options || []).map((w) => ({ ...w })),
      tags: (p.tags || []).join(", "),
      occasions: (p.occasions || []).join(", "),
    });
    setOpen(true);
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = (f.name || "").toLowerCase();
    const okExt = /\.(jpe?g|png|gif|webp|avif|heic|heif)$/i.test(name);
    if (!f.type.startsWith("image/") && !okExt) { toast.error("Please pick an image file (jpg/png/webp/avif/heic)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((prev) => ({ ...prev, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addWeightRow = () => setEditing((p) => ({ ...p, weight_options: [...(p.weight_options || []), { label: "", price: 0 }] }));
  const setWeightRow = (i, patch) => setEditing((p) => ({
    ...p,
    weight_options: p.weight_options.map((w, idx) => idx === i ? { ...w, ...patch } : w),
  }));
  const removeWeightRow = (i) => setEditing((p) => ({ ...p, weight_options: p.weight_options.filter((_, idx) => idx !== i) }));

  const save = async () => {
    try {
      const payload = {
        ...editing,
        price: Number(editing.price),
        weight_options: (editing.weight_options || [])
          .map((w) => ({ label: (w.label || "").trim(), price: Number(w.price) }))
          .filter((w) => w.label && !Number.isNaN(w.price)),
        tags: typeof editing.tags === "string" ? editing.tags.split(",").map((s) => s.trim()).filter(Boolean) : (editing.tags || []),
        occasions: typeof editing.occasions === "string" ? editing.occasions.split(",").map((s) => s.trim()).filter(Boolean) : (editing.occasions || []),
      };
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <div>
          <div className="overline">Catalog</div>
          <h1 className="font-display text-3xl mt-1">Products</h1>
        </div>
        <button data-testid="admin-add-product" className="btn-primary" onClick={openNew}><Plus className="w-4 h-4" /> Add Product</button>
      </div>

      <div className="mt-6 admin-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F2EC] text-left">
            <tr>
              <th className="p-4">Product</th>
              <th className="p-4">Category</th>
              <th className="p-4">Starting Price</th>
              <th className="p-4">Options</th>
              <th className="p-4">Stock</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody data-testid="admin-products-table">
            {products.map((p) => (
              <tr key={p.id} className="border-t border-[#E5DFD3]">
                <td className="p-4 flex items-center gap-3">
                  <img src={absImg(p.image)} alt="" className="w-10 h-10 rounded object-cover" />
                  <span>{p.name}</span>
                </td>
                <td className="p-4 capitalize">{p.category}</td>
                <td className="p-4">₹{p.price}</td>
                <td className="p-4 text-xs text-[#6C665F]">
                  {(p.weight_options || []).length > 0
                    ? p.weight_options.map((w) => `${w.label}·₹${w.price}`).join(" · ")
                    : "—"}
                </td>
                <td className="p-4">{p.in_stock ? "In stock" : "Out"}</td>
                <td className="p-4 flex gap-2">
                  <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:bg-[#F4F2EC] rounded"><Edit3 className="w-4 h-4" /></button>
                  <button data-testid={`del-${p.id}`} onClick={() => del(p.id)} className="p-2 hover:bg-[#F4F2EC] rounded text-[#8C3A3A]"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">{editing.id ? "Edit Product" : "New Product"}</div>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>

            {/* Image + Upload */}
            <div className="flex gap-4 items-start mb-4">
              <div className="w-28 h-28 rounded-xl overflow-hidden bg-[#F4F2EC] shrink-0 flex items-center justify-center">
                {editing.image
                  ? <img src={absImg(editing.image)} alt="preview" className="w-full h-full object-cover" />
                  : <span className="text-[#6C665F] text-xs">No image</span>}
              </div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*,.avif,.heic,.heif" onChange={upload} className="hidden" data-testid="pf-upload" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-outline inline-flex items-center gap-2 mb-2">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Upload className="w-4 h-4" /> Upload from computer</>}
                </button>
                <input placeholder="…or paste an image URL" className="input-eko" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} data-testid="pf-image" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Name" className="input-eko col-span-2" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="pf-name" />
              <select className="input-eko" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="pf-category">
                <option value="cakes">Cakes</option>
                <option value="flowers">Flowers</option>
                <option value="hampers">Hampers</option>
                <option value="mugs">Mugs</option>
                <option value="combos">Combos</option>
              </select>
              <input type="number" placeholder="Starting price ₹" className="input-eko" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="pf-price" />
              <textarea rows={3} placeholder="Description" className="input-eko col-span-2" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            </div>

            {/* Weight / Option pricing */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <div className="overline">Options & pricing (e.g. 0.5 Kg / 1 Kg / 2 Kg)</div>
                <button type="button" onClick={addWeightRow} className="text-sm text-[#8C3A3A] font-semibold inline-flex items-center gap-1" data-testid="pf-add-weight"><Plus className="w-3 h-3" /> Add option</button>
              </div>
              {(editing.weight_options || []).length === 0 && (
                <p className="text-xs text-[#6C665F] italic mb-2">No options — customers will pay the starting price above.</p>
              )}
              <div className="space-y-2">
                {(editing.weight_options || []).map((w, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_40px] gap-2 items-center" data-testid={`pf-weight-row-${i}`}>
                    <input placeholder="Label e.g. 1 Kg" className="input-eko" value={w.label} onChange={(e) => setWeightRow(i, { label: e.target.value })} />
                    <input type="number" placeholder="Price ₹" className="input-eko" value={w.price} onChange={(e) => setWeightRow(i, { price: e.target.value })} />
                    <button type="button" onClick={() => removeWeightRow(i)} className="p-2 text-[#8C3A3A] hover:bg-[#F4F2EC] rounded flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <input placeholder="Tags (comma-separated)" className="input-eko col-span-2" value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
              <input placeholder="Occasions (birthday, anniversary, valentine, rakhi)" className="input-eko col-span-2" value={editing.occasions} onChange={(e) => setEditing({ ...editing, occasions: e.target.value })} data-testid="pf-occasions" />
              <label className="flex items-center gap-2 col-span-2 text-sm"><input type="checkbox" checked={editing.in_stock} onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} /> In stock</label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="btn-outline">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="pf-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
